import { z } from 'zod';
import { assetKindSchema } from './enums.js';
/**
 * Contrats du DÉDOUBLONNAGE (T6, XPHOTO-08/-09/-23). Source unique front + back.
 *
 * Le dédoublonnage vise les QUASI-doublons (ré-encodages, redimensionnements,
 * retouches légères) : les doublons EXACTS (octets/pixels identiques) sont DÉJÀ
 * collapsés au scan via `unique(library_id, identity_hash)`. On regroupe donc les
 * assets par proximité PERCEPTUELLE de leur `phash` (dHash 64 bits), mesurée par
 * la distance de Hamming ; `threshold` = distance maximale (bits) pour lier deux
 * assets dans un même cluster.
 *
 * - `dedupRunRequestSchema`      : corps de `POST /api/libraries/:id/dedup` (analyse).
 * - `dedupMemberSchema`          : un asset membre d'un cluster (assez pour peindre
 *   une tuile + comparer résolution/taille).
 * - `dedupClusterSchema`         : un groupe de ≥2 quasi-doublons (keeper en tête).
 * - `dedupClustersResponseSchema`: page de clusters + compteurs.
 * - `dedupSummarySchema`         : résumé (badge/tête d'écran).
 * - `dedupApplyRequestSchema`    : corps de `POST /api/libraries/:id/dedup/apply`
 *   (décisions keep/delete par asset).
 * - `dedupApplyResultSchema`     : ventilation d'un job `dedup_apply`.
 *
 * SÉCURITÉ : ces schémas ne font QUE valider les entrées. La garde anti-traversée
 * DÉFINITIVE lors de la suppression reste le `safeJoin(root, rel)` côté job.
 */
// ---------------------------------------------------------------------------
// Méthode & bornes
// ---------------------------------------------------------------------------
/**
 * Méthode de clustering. Seul `phash` existe (ADR-0002 : `dedup_method` = `phash`).
 * Pas de méthode `'exact'` : les doublons exacts sont collapsés au scan.
 */
export const dedupMethodSchema = z.enum(['phash']);
/** Action décidée sur un membre d'un cluster (ADR-0002 : `dedup_action`). */
export const dedupActionSchema = z.enum(['keep', 'delete']);
/**
 * Seuil de distance de Hamming max (bits) : un `phash` fait 64 bits. Au-delà de 16
 * la notion de « quasi-doublon » perd son sens (trop permissif → faux positifs) ;
 * on borne donc à `[0, 16]`. Défaut 8 (compromis usuel dHash).
 */
export const DEDUP_THRESHOLD_MIN = 0;
export const DEDUP_THRESHOLD_MAX = 16;
export const DEDUP_THRESHOLD_DEFAULT = 8;
// ---------------------------------------------------------------------------
// Requête d'analyse (POST /api/libraries/:id/dedup)
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/libraries/:id/dedup`. Enfile un job `dedup` (clustering).
 * `threshold` = distance de Hamming max pour lier deux `phash`. `dryRun` : si vrai,
 * le clustering est calculé mais NON persisté (aperçu chiffré, aucune écriture) —
 * l'analyse n'étant de toute façon PAS destructive (elle ne touche que les tables
 * dedup, jamais /library).
 */
export const dedupRunRequestSchema = z.object({
    threshold: z
        .number()
        .int()
        .min(DEDUP_THRESHOLD_MIN)
        .max(DEDUP_THRESHOLD_MAX)
        .optional()
        .default(DEDUP_THRESHOLD_DEFAULT),
    dryRun: z.boolean().optional().default(false),
});
// ---------------------------------------------------------------------------
// Lecture des clusters (GET /api/libraries/:id/dedup/clusters)
// ---------------------------------------------------------------------------
/**
 * Un membre d'un cluster : l'asset et ce qu'il faut pour l'afficher (vignette via
 * `GET /api/assets/:id/thumbnail?size=256`) et le comparer (résolution, taille).
 */
export const dedupMemberSchema = z.object({
    assetId: z.uuid(),
    filename: z.string(),
    relPath: z.string(),
    kind: assetKindSchema,
    width: z.number().int().nonnegative().nullable(),
    height: z.number().int().nonnegative().nullable(),
    sizeBytes: z.number().int().nonnegative(),
    durationS: z.number().nullable(),
    /** Heuristique de qualité (résolution puis taille) : plus haut = meilleur. */
    qualityScore: z.number(),
    /** Le membre à conserver (un seul par cluster). */
    isKeeper: z.boolean(),
    /** Distance de Hamming 0 au keeper (quasi-identique perceptuel). */
    isExactDup: z.boolean(),
});
/**
 * Un cluster de quasi-doublons : ≥2 membres, keeper en tête (puis membres triés
 * par qualité décroissante).
 */
export const dedupClusterSchema = z.object({
    id: z.uuid(),
    method: dedupMethodSchema,
    threshold: z.number().int().min(DEDUP_THRESHOLD_MIN).max(DEDUP_THRESHOLD_MAX),
    members: z.array(dedupMemberSchema).min(2),
});
/**
 * Page de clusters. `total` = nombre total de clusters (pour la pagination) ;
 * `clusterCount` = idem (alias explicite pour l'entête d'écran) ; `duplicateCount`
 * = nombre de membres NON keeper de la page (candidats à supprimer).
 */
export const dedupClustersResponseSchema = z.object({
    clusters: z.array(dedupClusterSchema),
    total: z.number().int().nonnegative(),
    clusterCount: z.number().int().nonnegative(),
    duplicateCount: z.number().int().nonnegative(),
});
/** Bornes de pagination des clusters (query `limit`/`offset`). */
export const DEDUP_CLUSTERS_LIMIT_MAX = 50;
export const DEDUP_CLUSTERS_LIMIT_DEFAULT = 50;
/** Query de `GET /api/libraries/:id/dedup/clusters` : pagination bornée. */
export const dedupClustersQuerySchema = z.object({
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(DEDUP_CLUSTERS_LIMIT_MAX)
        .optional()
        .default(DEDUP_CLUSTERS_LIMIT_DEFAULT),
    offset: z.coerce.number().int().nonnegative().optional().default(0),
});
// ---------------------------------------------------------------------------
// Résumé (GET /api/libraries/:id/dedup/summary)
// ---------------------------------------------------------------------------
/**
 * Résumé du dernier clustering (badge/tête d'écran). `clusterCount` = nb de clusters
 * de la bibliothèque ; `duplicateCount` = nb total de membres non keeper ; `lastRunAt`
 * = date du dernier clustering (max `created_at`), ou `null` si jamais lancé.
 */
export const dedupSummarySchema = z.object({
    clusterCount: z.number().int().nonnegative(),
    duplicateCount: z.number().int().nonnegative(),
    lastRunAt: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Application des décisions (POST /api/libraries/:id/dedup/apply)
// ---------------------------------------------------------------------------
/** Une décision pour un asset : le garder ou le supprimer. */
export const dedupDecisionSchema = z.object({
    assetId: z.uuid(),
    action: dedupActionSchema,
});
/**
 * Corps de `POST /api/libraries/:id/dedup/apply`. `decisions` = liste NON VIDE de
 * décisions par asset. La SUPPRESSION est une ÉCRITURE : la route exige l'accès
 * écriture ET `LIBRARY_WRITABLE` (409 sinon). `dryRun` : aperçu, ne supprime rien.
 */
export const dedupApplyRequestSchema = z.object({
    decisions: z.array(dedupDecisionSchema).nonempty(),
    dryRun: z.boolean().optional().default(false),
});
/**
 * Ventilation d'un job `dedup_apply`. Chaque décision tombe dans une catégorie :
 *  - `deleted` : fichier supprimé (ou, en dry-run, qui SERAIT supprimé).
 *  - `kept`    : décision `keep` (aucune action disque).
 *  - `skipped` : fichier déjà absent du disque (ENOENT) — marqué `is_missing` quand même.
 *  - `failed`  : chemin refusé (traversée) ou asset introuvable dans la bibliothèque.
 */
export const dedupApplyResultSchema = z.object({
    total: z.number().int().nonnegative(),
    deleted: z.number().int().nonnegative(),
    kept: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Écarter des clusters « faux positifs » (POST /api/libraries/:id/dedup/dismiss)
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/libraries/:id/dedup/dismiss`. `clusterIds` = liste NON VIDE d'ids de
 * clusters « Images similaires » (pHash) que l'utilisateur déclare NON doublons → ils sont
 * masqués de la revue et du résumé (`dedup_clusters.dismissed_at` posé). SYNCHRONE (écriture
 * DB pure — comme les décisions de revue) : PAS de job, PAS de `LIBRARY_WRITABLE`.
 *
 * PAS de `.transform` (piège Zod v4 → boot crash de `z.toJSONSchema`, cf.
 * `route-json-schema.test.ts`).
 */
export const dismissDedupClustersRequestSchema = z.object({
    clusterIds: z.array(z.uuid()).min(1),
});
/** Résultat de l'écartement : nombre de clusters effectivement masqués (déjà écartés exclus). */
export const dismissDedupClustersResultSchema = z.object({
    dismissed: z.number().int().nonnegative(),
});
//# sourceMappingURL=dedup.js.map